import { useEffect, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle } from "lucide-react";
import { useCompanyInfo } from "@/lib/company";
import { SimulatorContext } from "@/context/SimulatorContext";

// Sanitize a raw phone string to a wa.me-compatible sequence of digits (no +).
function toWaNumber(raw) {
  if (!raw) return "";
  return String(raw).replace(/\D+/g, "");
}

/**
 * Floating WhatsApp button — visible on public pages when
 * company_settings.whatsapp_business_number is set. Deep-links to
 * https://wa.me/{number}?text={contextual message}.
 */
export default function WhatsAppFloat() {
  const { t } = useTranslation();
  const company = useCompanyInfo();
  const location = useLocation();
  const productMatch = useMatch("/catalogue/:category/:slug");
  const sim = useContext(SimulatorContext);  // null when not on simulator page

  const number = toWaNumber(company?.whatsapp_business_number);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => { setHydrated(true); }, []);

  // Hide entirely on admin pages or when number is not set.
  const isAdmin = location.pathname.startsWith("/admin");
  if (!hydrated || !number || isAdmin) return null;

  const message = buildMessage({ t, location, productMatch, sim, brand: company?.brand_name || "SolarSim" });
  const href = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float"
      data-wa-href={href}
      aria-label={t("whatsapp.tooltip")}
      title={t("whatsapp.tooltip")}
      className="group fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-lg shadow-[#25D366]/40 transition-transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#25D366]/40"
    >
      <MessageCircle className="h-6 w-6" strokeWidth={2.2} />
      <span className="pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-full bg-slate-900 px-3 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {t("whatsapp.tooltip")}
      </span>
    </a>
  );
}

/** Compose the contextual pre-filled message from the current URL and sim state. */

function buildMessage({ t, location, productMatch, sim, brand }) {
  const path = location.pathname || "/";
  if (productMatch) {
    // We don't have product brand/model here; derive from URL slug fallback.
    const slug = (productMatch.params?.slug || "").replace(/-/g, " ");
    return t("whatsapp.messages.product", { product: slug });
  }
  if (path.startsWith("/catalogue")) return t("whatsapp.messages.catalogue");
  if (path.startsWith("/simulateur") || path.startsWith("/simulator")) {
    const kwp = sim?.computed?.production?.peak_power_kwp;
    const kwh = sim?.computed?.production?.annual_kwh;
    if (kwp && kwh) {
      return t("whatsapp.messages.simulator_step4", {
        kwp: Number(kwp).toLocaleString("fr-FR", { maximumFractionDigits: 1 }),
        kwh: Number(kwh).toLocaleString("fr-FR", { maximumFractionDigits: 0 }),
      });
    }
    return t("whatsapp.messages.simulator_generic");
  }
  if (path === "/" || path === "") return t("whatsapp.messages.landing");
  return t("whatsapp.messages.generic", { brand: brand || "SolarSim" });
}
